import { apiFetch } from '../services/cloudflareService';
import React, { useState, useEffect } from 'react';
import { Activity, Server, Database, Globe, AlertTriangle, TrendingUp, RefreshCw, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { motion } from 'framer-motion';

interface CFMetricsData {
    summary: {
        requests: number;
        errors: number;
        cpuTime: number; // in milliseconds or microseconds depending on CF
    };
    series: {
        date: string;
        requests: number;
        errors: number;
        cpuTime: number;
    }[];
}

interface CloudflareMetricsProps {
    theme: 'dark' | 'light';
}

const CloudflareMetrics: React.FC<CloudflareMetricsProps> = ({ theme }) => {
    const [data, setData] = useState<CFMetricsData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const t = {
        bg: theme === 'dark' ? 'bg-slate-900' : 'bg-slate-50',
        textMain: theme === 'dark' ? 'text-slate-300' : 'text-slate-600',
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-slate-800/40' : 'bg-white',
        cardBorder: theme === 'dark' ? 'border-white/10' : 'border-slate-200',
        cardShadow: theme === 'dark' ? 'shadow-none' : 'shadow-sm',
        accentBg: theme === 'dark' ? 'bg-[#F6821F]/10' : 'bg-[#F6821F]/10',
        accentText: theme === 'dark' ? 'text-[#F6821F]' : 'text-[#D06000]',
        accentBorder: theme === 'dark' ? 'border-[#F6821F]/30' : 'border-[#F6821F]/30',
        gridLines: theme === 'dark' ? '#334155' : '#e2e8f0',
        tooltipBg: theme === 'dark' ? '#1e293b' : '#ffffff',
        tooltipBorder: theme === 'dark' ? '#334155' : '#e2e8f0',
        tooltipText: theme === 'dark' ? '#f8fafc' : '#0f172a'
    };

    const fetchMetrics = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await apiFetch('/api/admin/metrics/cloudflare', {credentials: 'include'});
            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || `HTTP ${res.status}`);
            }
            const json = await res.json();
            if (json.error) {
                throw new Error(json.error);
            }
            setData(json);
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Error al obtener métricas de Cloudflare. Revisa el CF_API_TOKEN.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();
    }, []);

    const formatNumber = (num: number) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(num);

    // Plan Limits ($5 Plan)
    const WORKER_LIMIT = 10_000_000;
    const KV_READ_LIMIT = 10_000_000; // Just illustrative if API limits KV

    if (isLoading) {
        return (
            <div className={`p-8 animate-pulse space-y-6`}>
                <div className={`h-8 w-64 bg-slate-700/20 rounded-lg`}></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className={`h-32 bg-slate-700/20 rounded-2xl`}></div>
                    <div className={`h-32 bg-slate-700/20 rounded-2xl`}></div>
                    <div className={`h-32 bg-slate-700/20 rounded-2xl`}></div>
                </div>
                <div className={`h-64 bg-slate-700/20 rounded-2xl`}></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`p-8`}>
                <div className={`p-6 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-start gap-4`}>
                    <AlertCircle className={`w-6 h-6 text-red-400 mt-1 flex-shrink-0`} />
                    <div>
                        <h3 className={`text-red-400 font-bold text-lg mb-1`}>Error de Conexión</h3>
                        <p className={`text-sm ${t.textMain}`}>{error}</p>
                        <p className={`text-xs ${t.textMuted} mt-4`}>Asegúrate de haber configurado <code>CF_ACCOUNT_ID</code> y el secreto <code>CF_API_TOKEN</code> en Wrangler.</p>
                        <button onClick={fetchMetrics} className={`mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm font-bold transition-colors flex items-center gap-2`}>
                            <RefreshCw className="w-4 h-4" /> Reintentar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const { summary, series } = data || { summary: { requests: 0, errors: 0, cpuTime: 0 }, series: [] };

    // Format for Recharts
    const chartData = series.map(s => ({
        ...s,
        dateFormatted: new Date(s.date).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })
    }));

    const progressPercentage = Math.min((summary.requests / WORKER_LIMIT) * 100, 100);

    return (
        <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className={`space-y-6 ${t.textMain}`}
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${t.accentBg} ${t.accentBorder} border`}>
                        <Globe className={`w-6 h-6 ${t.accentText}`} />
                    </div>
                    <div>
                        <h2 className={`text-xl font-bold ${t.textWhite} leading-tight`}>Estadísticas de Tráfico Cloudflare</h2>
                        <p className={`text-xs ${t.textMuted}`}>Últimos 30 días según el panel Edge Network</p>
                    </div>
                </div>
                <button onClick={fetchMetrics} className={`p-2 rounded-lg ${t.cardBg} border ${t.cardBorder} hover:bg-slate-700/50 transition-colors`}>
                    <RefreshCw className={`w-4 h-4 ${t.textMuted}`} />
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className={`${t.cardBg} border ${t.cardBorder} ${t.cardShadow} rounded-3xl p-6 relative overflow-hidden group`}>
                    <div className="flex justify-between items-start mb-4">
                        <div className={`p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 group-hover:scale-110 transition-transform`}>
                            <Activity className="w-6 h-6" />
                        </div>
                        <span className={`text-xs font-bold text-blue-400 bg-blue-500/10 px-2 py-1 rounded border border-blue-500/20`}>Workers Invocations</span>
                    </div>
                    <p className={`text-3xl font-bold ${t.textWhite}`}>{summary.requests.toLocaleString()}</p>
                    <p className={`text-xs ${t.textMuted} mt-1`}>Peticiones Totales (30d)</p>
                    
                    {/* Plan Progress Bar */}
                    <div className="mt-4">
                        <div className={`flex justify-between text-[10px] font-bold mb-1 ${t.textMuted}`}>
                            <span>Consumo de Plan ($5)</span>
                            <span>{formatNumber(WORKER_LIMIT)} Límite</span>
                        </div>
                        <div className={`h-1.5 w-full bg-slate-700/50 rounded-full overflow-hidden`}>
                            <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercentage}%` }}
                                transition={{ duration: 1, ease: 'easeOut' }}
                                className={`h-full ${progressPercentage > 90 ? 'bg-red-500' : progressPercentage > 75 ? 'bg-amber-500' : 'bg-blue-500'}`} 
                            />
                        </div>
                    </div>
                </div>

                <div className={`${t.cardBg} border ${t.cardBorder} ${t.cardShadow} rounded-3xl p-6 relative overflow-hidden group`}>
                    <div className="flex justify-between items-start mb-4">
                        <div className={`p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 group-hover:scale-110 transition-transform`}>
                            <Server className="w-6 h-6" />
                        </div>
                        <span className={`text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20`}>CPU Time</span>
                    </div>
                    <p className={`text-3xl font-bold ${t.textWhite}`}>{formatNumber(summary.cpuTime / 1000)} <span className="text-sm">sec</span></p>
                    <p className={`text-xs ${t.textMuted} mt-1`}>Tiempo de Ejecución Global</p>
                </div>

                <div className={`${t.cardBg} border ${t.cardBorder} ${t.cardShadow} rounded-3xl p-6 relative overflow-hidden group`}>
                    <div className="flex justify-between items-start mb-4">
                        <div className={`p-3 rounded-xl ${summary.errors > 0 ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'} group-hover:scale-110 transition-transform`}>
                            {summary.errors > 0 ? <AlertTriangle className="w-6 h-6" /> : <Database className="w-6 h-6" />}
                        </div>
                        <span className={`text-xs font-bold ${summary.errors > 0 ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'} px-2 py-1 rounded border`}>Excepciones</span>
                    </div>
                    <p className={`text-3xl font-bold ${summary.errors > 0 ? 'text-amber-400' : t.textWhite}`}>{summary.errors.toLocaleString()}</p>
                    <p className={`text-xs ${t.textMuted} mt-1`}>Errores de Worker (30d)</p>
                </div>
            </div>

            {/* Main Chart */}
            <div className={`${t.cardBg} border ${t.cardBorder} ${t.cardShadow} rounded-3xl p-6`}>
                <div className="flex items-center gap-2 mb-6">
                    <TrendingUp className={`w-5 h-5 ${t.textMuted}`} />
                    <h3 className={`text-sm font-bold ${t.textWhite} uppercase tracking-wider`}>Tendencia de Peticiones</h3>
                </div>
                
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorReq" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={t.gridLines} />
                            <XAxis 
                                dataKey="dateFormatted" 
                                stroke={t.textMuted} 
                                fontSize={10} 
                                tickLine={false}
                                axisLine={false}
                                minTickGap={20}
                            />
                            <YAxis 
                                stroke={t.textMuted} 
                                fontSize={10} 
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(val) => formatNumber(val)}
                            />
                            <Tooltip 
                                contentStyle={{ backgroundColor: t.tooltipBg, borderColor: t.tooltipBorder, color: t.tooltipText, borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}
                                itemStyle={{ color: '#3b82f6' }}
                            />
                            <Area 
                                type="monotone" 
                                dataKey="requests" 
                                name="Requests"
                                stroke="#3b82f6" 
                                strokeWidth={3}
                                fillOpacity={1} 
                                fill="url(#colorReq)" 
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

        </motion.div>
    );
};

export default CloudflareMetrics;
